import type { TimeZone } from '@iheartjane/types';
import type { Dayjs } from 'dayjs';
import findLast from 'lodash-es/findLast';

import type { StoreHours } from '@jane/shared/types';
import { parseCurrentTime } from '@jane/shared/util';

import type { ScheduleType } from './orderingSchedule';
import { buildExtendedScheduleDays } from './storeScheduleDay';
import type { StoreScheduleDay } from './storeScheduleDay';

export const SCHEDULE_TYPE: { [key: string]: ScheduleType } = {
  curbside: 'curbside',
  delivery: 'delivery',
  pickup: 'pickup',
  retail: 'retail',
};

enum Day {
  Friday = 'friday',
  Monday = 'monday',
  Saturday = 'saturday',
  Sunday = 'sunday',
  Thursday = 'thursday',
  Tuesday = 'tuesday',
  Wednesday = 'wednesday',
}

const NEARLY_OPEN_OFFSET = 30;

export interface StoreSchedule {
  getNextOpenDay: () => StoreScheduleDay | undefined;
  getToday: () => StoreScheduleDay;
  isAlwaysClosed: () => boolean;
  isCurrentlyClosed: () => boolean;
  isCurrentlyOpen: () => boolean;
  isNearlyOpen: () => boolean;
  scheduleDays: StoreScheduleDay[];
  type: ScheduleType;
  willOpenLaterToday: () => boolean;
}

/**
 * Creates a StoreSchedule object with computed properties based on schedule data
 *
 * NOTE: This is a util ONLY, see buildStoreSchedule for the main entry point.
 *
 * This function takes an array of schedule days and builds a StoreSchedule with
 * dynamically calculated properties such as current open status, next available day,
 * and near-opening detection.
 */
export function buildSchedule({
  scheduleDays,
  timeZoneIdentifier,
  type,
}: {
  scheduleDays: StoreScheduleDay[];
  timeZoneIdentifier: TimeZone;
  type: ScheduleType;
}): StoreSchedule {
  const getCurrentTime = () => parseCurrentTime(timeZoneIdentifier);

  const findByName = (dayName: Day) => {
    return scheduleDays.find((day) => day.dayName === dayName);
  };

  const findCurrentWorkingDay = (time: Dayjs) => {
    return findLast(scheduleDays, (day) => day.isTimeDuringDay(time));
  };

  const isAlwaysClosed = () => {
    return scheduleDays.every((workingDay) => workingDay.isClosedAllDay);
  };

  const todaysDayName = () => {
    return getCurrentTime().format('dddd').toLowerCase() as Day;
  };

  const getToday = () => {
    const time = getCurrentTime();
    const today = findCurrentWorkingDay(time) || findByName(todaysDayName());

    if (!today) {
      throw new Error(`Could not find day in schedule for ${todaysDayName()}`);
    }
    return today;
  };

  const isCurrentlyOpen = () => {
    return !!findCurrentWorkingDay(getCurrentTime());
  };

  const isCurrentlyClosed = () => {
    return !isCurrentlyOpen();
  };

  const isNearlyOpen = () => {
    return (
      !isCurrentlyOpen() &&
      !!findCurrentWorkingDay(
        getCurrentTime().add(NEARLY_OPEN_OFFSET, 'minutes')
      )
    );
  };

  const getNextOpenDay = () => {
    if (isAlwaysClosed()) return undefined;

    const today = getToday();
    const todayIndex = scheduleDays.indexOf(today);

    const beforeDays = scheduleDays.slice(0, todayIndex + 1);
    const restOfWeek = scheduleDays.slice(todayIndex + 1, scheduleDays.length);

    return restOfWeek.concat(beforeDays).find((day) => day.isOpenPartOfTheDay);
  };

  const willOpenLaterToday = () => {
    return !!getToday().willOpenLater(getCurrentTime());
  };

  return {
    getNextOpenDay,
    getToday,
    isAlwaysClosed,
    isCurrentlyClosed,
    isCurrentlyOpen,
    isNearlyOpen,
    scheduleDays,
    type,
    willOpenLaterToday,
  };
}

/**
 * Finds the index of the schedule day that contains the specified start time
 */
export const currentDayIndex = (
  scheduleDays: StoreScheduleDay[],
  startTime: Dayjs
): number => scheduleDays.findIndex((day) => day.isTimeDuringDay(startTime));

/**
 * Extracts a 7-day valid schedule from a longer schedule array
 *
 * This function processes an array of schedule days (typically 8 days starting from yesterday)
 * and returns a 7-day schedule starting from the current day. If the current time falls within
 * a specific day in the schedule, it uses that day as the starting point. Otherwise, it defaults
 * to removing just the first day (yesterday's duplicate) from the schedule.
 */
export const getValidScheduleDays = (
  extendedScheduleDays: StoreScheduleDay[],
  startTime: Dayjs
): StoreScheduleDay[] => {
  const firstDayIndex = currentDayIndex(extendedScheduleDays, startTime);

  if (firstDayIndex > -1) {
    return extendedScheduleDays.slice(firstDayIndex, firstDayIndex + 7);
  }

  return extendedScheduleDays.slice(1, extendedScheduleDays.length);
};

/**
 * Creates a complete store schedule based on working hours and the current time
 *
 * This function orchestrates the entire process of building a valid store schedule:
 * 1. Determines the current time in the specified timezone
 * 2. Generates an extended 8-day schedule starting from yesterday (with that same day duplicated at the end)
 * 3. Extracts a valid 7-day schedule starting from the current day
 * 4. Builds and returns the final StoreSchedule object with computed properties
 *
 * The resulting schedule includes day-by-day information and helper properties like
 * isCurrentlyOpen, nextOpenDay, and other status indicators relative to the current time.
 * @param timeZoneIdentifier - String identifier for the store's timezone (e.g., "America/Los_Angeles")
 * @param type - The type of schedule being built (Pickup, Delivery, Retail, or Curbside)
 * @param workingHours - Array of working hour configurations from the store
 * @returns A complete StoreSchedule object with current status and 7-day schedule
 */
export function buildStoreSchedule({
  timeZoneIdentifier,
  type,
  workingHours,
}: {
  timeZoneIdentifier: TimeZone;
  type: ScheduleType;
  workingHours: StoreHours[];
}): StoreSchedule {
  const startTime = parseCurrentTime(timeZoneIdentifier);
  const yesterday = startTime.clone().subtract(1, 'day');

  const extendedScheduleDays = buildExtendedScheduleDays({
    workingHours,
    yesterday,
  });

  const storeScheduleDays = getValidScheduleDays(
    extendedScheduleDays,
    startTime
  );

  return buildSchedule({
    scheduleDays: storeScheduleDays,
    timeZoneIdentifier,
    type,
  });
}
